import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { authService } from '../services/authService';
import { getPlatform, isMobile, createPlatformListener } from '../utils/platform';
import { getErrorMessage } from '../utils/errorMessages';
import { clearCache as clearApiCache } from '../utils/apiCache';

// Used to detect account switches. Stored in localStorage under this key so
// we can compare the previous logged-in email against the one just entered.
const LAST_USER_KEY = 'pcc_last_user_email';

/**
 * Dispatches a window-level event that DataCacheProvider + any cache-aware
 * component can listen to. Also clears the util apiCache + localStorage
 * token/user. Use on logout and on account-switch login.
 *
 * IMPORTANT: keys with the `pcc_onboarding_` prefix are PRESERVED. The
 * onboarding "already seen" flag must survive logout so returning users
 * don't see the tutorial again.
 */
const nukeAllCaches = () => {
  try { clearApiCache(); } catch (_) {}
  try {
    // Only remove session-related keys; preserve onboarding flags + any
    // other persistent per-user preferences that need to survive logout.
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    // (pcc_onboarding_* keys are deliberately left untouched)
  } catch (_) {}
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pcc:clear-cache'));
    }
  } catch (_) {}
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Role hierarchy: dg > admin > leader > member > visitor
const ROLE_HIERARCHY = {
  visitor: 0,
  member: 1,
  leader: 2,
  admin: 3,
  dg: 4
};

// Role labels in French
export const ROLE_LABELS = {
  visitor: 'Visiteur',
  member: 'Membre',
  leader: 'Responsable',
  admin: 'Administrateur',
  dg: 'Directeur Général',
};

// Home path per role
export const ROLE_HOME = {
  visitor: '/',
  member: '/member/home',
  leader: '/leader/dashboard',
  admin: '/dg/dashboard',
  dg: '/dg/dashboard',
};

// Quick-switch session storage key
const QS_KEY = 'pcc_quick_switch_origin';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState('visitor');
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState(getPlatform());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Quick-switch state: stores origin admin info when in a test account
  const [quickSwitchOrigin, setQuickSwitchOrigin] = useState(() => {
    try {
      const stored = sessionStorage.getItem(QS_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // Track platform changes
  useEffect(() => {
    const cleanup = createPlatformListener((newPlatform) => {
      setPlatform(newPlatform);
    });
    return cleanup;
  }, []);

  // Check auth status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          setUser(storedUser);
          const r = storedUser.role || 'visitor';
          setRole(r);
          setIsAuthenticated(true);
        }

        const authStatus = await authService.checkAuth();
        if (authStatus.isAuthenticated && authStatus.user) {
          setUser(authStatus.user);
          const r = authStatus.role;
          setRole(r);
          setIsAuthenticated(true);
          authService.updateStoredUser(authStatus.user);
        } else if (!storedUser) {
          setUser(null);
          setRole('visitor');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          setUser(storedUser);
          const r = storedUser.role || 'visitor';
          setRole(r);
          setIsAuthenticated(true);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = useCallback(async (credentials) => {
    try {
      // Account-switch detection: if the previous session's email differs
      // from the one being entered, nuke ALL caches. This is the permanent
      // fix for "wrong user profile shown on login" — stale data from the
      // previous account can never leak into the new session.
      let previousEmail = null;
      try { previousEmail = localStorage.getItem(LAST_USER_KEY); } catch (_) {}
      const incomingEmail = (credentials?.email || '').trim().toLowerCase();
      const isAccountSwitch = previousEmail && incomingEmail && previousEmail !== incomingEmail;
      if (isAccountSwitch || previousEmail === null) {
        nukeAllCaches();
      } else {
        // Same user re-login — still clear stale localStorage but keep caches
        try {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        } catch (_) {}
      }

      const data = await authService.login(credentials);
      if (data.success && data.user) {
        setUser(data.user);
        const r = data.user.role;
        setRole(r);
        setIsAuthenticated(true);
        try { authService.updateStoredUser(data.user); } catch (_) {}
        try { localStorage.setItem(LAST_USER_KEY, incomingEmail); } catch (_) {}
        return { success: true, user: data.user };
      }
      return { success: false, error: data.message || 'Email ou mot de passe incorrect.' };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: getErrorMessage(error, 'login')
      };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const data = await authService.register(userData);
      return { success: true, data };
    } catch (error) {
      // Full structured log for debugging
      console.error('[Register] error object:', error);
      console.error('[Register] response status:', error?.response?.status);
      console.error('[Register] response data:', error?.response?.data);

      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;

      // Map Pydantic field paths + our custom field names → frontend field keys
      const FIELD_MAP = {
        email: 'email',
        password: 'password',
        firstName: 'firstName',
        lastName: 'lastName',
        gender: 'gender',
        postalCode: 'postalCode',
        country: 'country',
        rgpdConsent: 'rgpdConsent',
      };

      // Map Pydantic error type → French message for a given field
      const pydanticErrorMessage = (field, type, msg) => {
        if (type?.startsWith('value_error.email') || type === 'value_error' && field === 'email') {
          return "Format d'email invalide";
        }
        if (type?.includes('missing') || type === 'value_error.missing') {
          return 'Ce champ est obligatoire';
        }
        if (type?.includes('string_too_short') || type?.includes('min_length')) {
          if (field === 'password') return 'Le mot de passe doit contenir au moins 8 caractères';
          return 'Ce champ est trop court';
        }
        if (type?.includes('string_pattern_mismatch') || type === 'value_error.str.regex') {
          if (field === 'postalCode') return 'Le code postal doit contenir 5 chiffres';
          return 'Format invalide';
        }
        return msg || 'Valeur invalide';
      };

      const fieldErrors = {};
      let generalMessage = null;

      // 1) Our own structured error (409 EMAIL_EXISTS, 500 SERVER_ERROR, etc.)
      if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const code = detail.code;
        const field = FIELD_MAP[detail.field];
        const message = detail.message;
        if (code === 'EMAIL_EXISTS' || status === 409) {
          fieldErrors.email = message || 'Cette adresse email est déjà utilisée';
        } else if (field && message) {
          fieldErrors[field] = message;
        } else if (message) {
          generalMessage = message;
        }
      }
      // 2) Pydantic validation errors (422) — detail is an array of {loc, msg, type}
      else if (Array.isArray(detail)) {
        for (const err of detail) {
          // err.loc is typically ["body", "fieldName"]
          const rawField = err.loc?.[err.loc.length - 1];
          const field = FIELD_MAP[rawField];
          if (field && !fieldErrors[field]) {
            fieldErrors[field] = pydanticErrorMessage(field, err.type, err.msg);
          }
        }
        if (Object.keys(fieldErrors).length === 0) {
          generalMessage = 'Les informations envoyées sont invalides.';
        }
      }
      // 3) String detail (legacy format)
      else if (typeof detail === 'string') {
        generalMessage = detail;
      }
      // 4) Fallback
      else {
        generalMessage = getErrorMessage(error, 'register');
      }

      return {
        success: false,
        error: generalMessage || 'Erreur lors de l\'inscription',
        fieldErrors,
        status,
        debug: detail?.debug || error?.message,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    // Always clear local state + ALL caches, even if the API logout fails.
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Nuke every cache that could surface stale previous-user data
    nukeAllCaches();
    try { localStorage.removeItem(LAST_USER_KEY); } catch (_) {}

    setUser(null);
    setRole('visitor');
    setIsAuthenticated(false);
    // Clear quick-switch on full logout
    sessionStorage.removeItem(QS_KEY);
    setQuickSwitchOrigin(null);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      const data = await authService.forgotPassword(email);
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, 'forgot-password')
      };
    }
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const data = await authService.resetPassword(token, newPassword);
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, 'reset-password')
      };
    }
  }, []);

  const verifyEmail = useCallback(async (token) => {
    try {
      const data = await authService.verifyEmail(token);
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, 'reset-password')
      };
    }
  }, []);

  /** Update user object in state and localStorage (e.g. after photo upload) */
  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const next = { ...prev, ...updates };
      authService.updateStoredUser(next);
      return next;
    });
  }, []);

  // ═══ Quick Account Switch (replaces old preview mode) ═══

  /**
   * Switch to a test account. Saves current DG/admin credentials
   * in sessionStorage so we can return instantly.
   */
  const quickSwitch = useCallback(async (testEmail, testPassword) => {
    // Save current admin session
    const originData = {
      token: localStorage.getItem('token'),
      user: localStorage.getItem('user'),
      refreshToken: localStorage.getItem('refreshToken'),
      displayName: user?.displayName || user?.firstName || 'Admin',
      email: user?.email,
      role: role,
    };
    sessionStorage.setItem(QS_KEY, JSON.stringify(originData));
    setQuickSwitchOrigin(originData);

    // Logout current session silently (don't hit server — just clear local)
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');

    // Login as test account
    const result = await login({ email: testEmail, password: testPassword });

    // Self-heal: if the test login failed (account missing in this env, wrong
    // password, rate-limit…), don't leave the admin logged out in a broken
    // half-switched state — restore the origin session we just stashed.
    if (!result?.success) {
      try {
        if (originData.token) localStorage.setItem('token', originData.token);
        if (originData.user) localStorage.setItem('user', originData.user);
        if (originData.refreshToken) localStorage.setItem('refreshToken', originData.refreshToken);
      } catch (_) {}
      sessionStorage.removeItem(QS_KEY);
      setQuickSwitchOrigin(null);
      try {
        const originUser = originData.user ? JSON.parse(originData.user) : null;
        if (originUser) {
          setUser(originUser);
          setRole(originData.role);
          setIsAuthenticated(true);
        }
      } catch (_) {}
    }
    return result;
  }, [user, role, login]);

  /**
   * Establish an authenticated session from tokens already issued by the
   * backend (e.g. visitor "register-and-book" flow returns a token + user
   * after creating the account, so we don't need to hit /login again).
   */
  const setAuthenticatedSession = useCallback((userData, token) => {
    if (!userData) return;
    try {
      if (token) localStorage.setItem('token', token);
      authService.updateStoredUser(userData);
      const incomingEmail = (userData?.email || '').trim().toLowerCase();
      if (incomingEmail) localStorage.setItem(LAST_USER_KEY, incomingEmail);
    } catch (_) { /* ignore storage errors */ }
    setUser(userData);
    setRole(userData.role || 'visitor');
    setIsAuthenticated(true);
  }, []);

  /**
   * Return to the original DG/admin account from a test account.
   * Restores the saved token + user without hitting the login API.
   */
  const returnToOrigin = useCallback(async () => {
    const origin = quickSwitchOrigin;
    if (!origin) return;

    // Clear current test session
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');

    // Restore origin session
    localStorage.setItem('token', origin.token);
    localStorage.setItem('user', origin.user);
    if (origin.refreshToken) localStorage.setItem('refreshToken', origin.refreshToken);

    const originUser = JSON.parse(origin.user);
    setUser(originUser);
    setRole(origin.role);
    setIsAuthenticated(true);

    // Clear quick-switch state
    sessionStorage.removeItem(QS_KEY);
    setQuickSwitchOrigin(null);

    // Drop the test account's cached API data so the restored admin doesn't
    // see stale member/leader data left in the query cache.
    try { clearApiCache(); } catch (_) {}
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('pcc:clear-cache'));
      }
    } catch (_) {}
  }, [quickSwitchOrigin]);

  /** Is the user currently in a quick-switched test session? */
  const isQuickSwitched = !!quickSwitchOrigin;

  // ═══ Role checking helpers ═══
  const hasRole = useCallback((requiredRole) => {
    return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[requiredRole];
  }, [role]);

  const isVisitor = useCallback(() => role === 'visitor', [role]);
  const isMember = useCallback(() => hasRole('member'), [hasRole]);
  const isLeader = useCallback(() => hasRole('leader'), [hasRole]);
  const isAdmin = useCallback(() => hasRole('admin'), [hasRole]);
  const isDG = useCallback(() => role === 'dg', [role]);

  const hasPermission = useCallback((permission) => {
    if (['admin', 'dg'].includes(role)) return true;
    return (user?.permissions || []).includes(permission);
  }, [user, role]);

  // Real role checks
  const realRole = role;
  const isRealAdmin = useCallback(() => ROLE_HIERARCHY[role] >= ROLE_HIERARCHY['admin'], [role]);
  const isRealDG = useCallback(() => role === 'dg', [role]);

  // Platform helpers
  const isMobilePlatform = useCallback(() => platform === 'mobile', [platform]);
  const isWebPlatform = useCallback(() => platform === 'web', [platform]);

  const canAccess = useCallback((requiredRole, allowedPlatforms = ['mobile', 'web']) => {
    if (!hasRole(requiredRole)) return false;
    if (!allowedPlatforms.includes(platform)) return false;
    return true;
  }, [hasRole, platform]);

  // ═══ Backwards-compatible stubs for old preview mode consumers ═══
  // These prevent crashes in any component that still references them
  const previewMode = false;
  const activeRole = role;
  const startPreview = useCallback(() => {}, []);
  const exitPreview = useCallback(() => {}, []);
  const switchActiveRole = useCallback(() => {}, []);
  const getAvailableRoles = useCallback(() => [], []);

  const value = useMemo(() => ({
    user,
    role,
    realRole,
    loading,
    isAuthenticated,

    // Quick-switch (replaces preview mode)
    quickSwitch,
    returnToOrigin,
    isQuickSwitched,
    quickSwitchOrigin,

    // Backwards-compatible stubs (preview mode — now no-ops)
    previewMode,
    activeRole,
    startPreview,
    exitPreview,
    switchActiveRole,
    getAvailableRoles,

    // Platform
    platform,
    isMobile: isMobilePlatform,
    isWeb: isWebPlatform,

    // Auth actions
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    updateUser,
    setAuthenticatedSession,

    // Role checks
    hasRole,
    isVisitor,
    isMember,
    isLeader,
    isAdmin,
    isDG,
    hasPermission,

    // Real role checks
    isRealAdmin,
    isRealDG,

    // Access control
    canAccess,

    // Current user helpers
    currentUser: user,
  }), [
    user, role, realRole, loading, isAuthenticated,
    quickSwitch, returnToOrigin, isQuickSwitched, quickSwitchOrigin,
    previewMode, activeRole, startPreview, exitPreview, switchActiveRole, getAvailableRoles,
    platform, isMobilePlatform, isWebPlatform,
    login, register, logout, forgotPassword, resetPassword, verifyEmail, updateUser, setAuthenticatedSession,
    hasRole, isVisitor, isMember, isLeader, isAdmin, isDG, hasPermission,
    isRealAdmin, isRealDG, canAccess,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
